import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutContent } from '@/mocks/aboutData';
import { testimonials, siteInfo, contactInfo } from '@/mocks/homeData';

export default function AboutPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于禁漫天堂',
    description: '了解禁漫天堂的品牌故事、平台使命、发展历程与用户口碑。',
    url: 'https://jmtthub.com/about',
    about: {
      '@type': 'Organization',
      name: '禁漫天堂',
      description: siteInfo.description,
      url: 'https://jmtthub.com/',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-6 pt-6 pb-4">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Page Header */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于禁漫天堂
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8 md:p-12">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 flex-shrink-0">
                  <i className="ri-book-open-line text-primary-500 text-lg"></i>
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground-900">
                  {aboutContent.brandStory.title}
                </h2>
              </div>

              <div className="relative">
                <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-primary-300 via-accent-300 to-transparent hidden md:block"></div>
                <div className="space-y-6">
                  {aboutContent.brandStory.paragraphs.map((para, index) => (
                    <div key={index} className="md:pl-8 relative">
                      <div className="hidden md:block absolute left-0 top-2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-accent-500 ring-4 ring-background-50"></div>
                      <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                        {para}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Mission */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {aboutContent.mission.title}
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                以用户需求为核心，持续创造价值
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutContent.mission.items.map((item, index) => (
                <div
                  key={index}
                  className="group bg-background-50 rounded-xl border border-background-200/70 p-6 hover:border-accent-300 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 group-hover:bg-accent-500 transition-colors duration-300 mb-4">
                    <i className={`${item.icon} text-accent-600 group-hover:text-white text-xl transition-colors`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                发展历程
              </h2>
              <p className="mt-2 text-sm text-foreground-500">一路走来的重要里程碑</p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-6">
                {aboutContent.milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="absolute left-4 md:left-1/2 top-1.5 -translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 ring-4 ring-background-100 z-10"></div>
                    <div className={`pl-10 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-background-50 border border-background-200/70">
                        <span className="text-lg font-heading font-bold text-primary-500">{milestone.year}</span>
                      </div>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{milestone.event}</p>
                    </div>
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                用户口碑
              </h2>
              <p className="mt-2 text-sm text-foreground-500">来自真实用户的高度评价</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-5"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-heading font-bold text-sm flex-shrink-0">
                      {t.avatar}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.date}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                联系我们
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                如有任何问题或建议，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 mx-auto mb-3">
                  <i className="ri-mail-line text-primary-500 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">电子邮箱</h3>
                <a href={`mailto:${contactInfo.email}`} className="text-xs text-primary-500 hover:underline">
                  {contactInfo.email}
                </a>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mx-auto mb-3">
                  <i className="ri-wechat-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-500">{contactInfo.wechat}</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 mx-auto mb-3">
                  <i className="ri-qq-line text-secondary-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">QQ群</h3>
                <p className="text-xs text-foreground-500">{contactInfo.qq}</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 mx-auto mb-3">
                  <i className="ri-time-line text-primary-500 text-xl"></i>
                </div>
                <h3 className="text-sm font-medium text-foreground-800 mb-1">工作时间</h3>
                <p className="text-xs text-foreground-500">{contactInfo.workingHours}</p>
              </div>
            </div>

            <div className="mt-8 bg-background-50 rounded-xl border border-background-200/70 p-6 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-50 mx-auto mb-4">
                <i className="ri-heart-line text-primary-500 text-2xl"></i>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-lg mx-auto">
                感谢您选择禁漫天堂。我们将持续努力，为您提供更丰富的漫画资源和更优质的阅读体验。
                您的满意是我们前进的最大动力！
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}